import type { QueryReturnValue } from "@reduxjs/toolkit/query";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../store";
import { toSingleLine } from "../../common/util/format";
import { accAPI } from "..";
import {
  ProfileParams,
  ProfileResponse,
  ProfileTransformResponse,
  SponsorProfileResponse,
  SponsorProfileTransformResponse,
} from "./profileAPI.types";

const getGraphqlPath = (api: { getState: () => unknown }, suffix: string) => {
  const state = api.getState() as RootState;
  const graphqlBase = state.boot.configuration.apiBasePaths?.graphql ?? "/gql";
  return `${graphqlBase}${suffix}`;
};

export const profileAPI = accAPI.injectEndpoints({
  endpoints: (build) => ({
    getProfile: build.query<ProfileTransformResponse, ProfileParams>({
      queryFn: async ({ period, affAbo }, api, _extra, baseQuery) => {
        const query = `{
          aboDetail(period:"${period}",affAbo:"${affAbo}"){
            aboInfo {
              affNo
              aboNo
              name
              localName
              isoLanguageCode
              privacyFlag
              isNew
              isNewInCurrentMonth
              isNewMigrated
              entryDate
              joinedDate
              countryCode
              verifiedCustomerFlag
              businessNatureCode
              isoLanguageCode
              isoBirthdate
              renewalDate
              registrationChannelCd
            }
            partyInfo {
              partyId
              localeDisplayName
              displayName
              localeName {
                givenName
                familyName
              }
              latinName {
                givenName
                familyName
              }
              isPrimary
              gender
              isoBirthdate
              preferredLanguage
              preferredLanguageCode
              phoneNumbers {
                displayNumber
                extension
                number
                outOfCountryCallingNumber
              }
              emailAddresses {
                email
                type
              }
              addresses {
                addressLines
                municipality
                region
                postalCode
                country
                type
              }
            }
          }
        }`;
        const queryParams = new URLSearchParams({ query: toSingleLine(query) });
        const result = (await baseQuery(getGraphqlPath(api, `/los/graphql?${queryParams}`))) as QueryReturnValue<
          ProfileResponse,
          FetchBaseQueryError,
          undefined
        >;
        if (result.error) return { error: result.error };
        const response = result.data;
        if (response) {
          const {
            data: {
              aboDetail: { aboInfo, partyInfo },
            },
          } = response;
          return { data: { aboInfo, partyInfo } };
        }
        return { data: {} };
      },
    }),
    getContactInfo: build.query<ProfileTransformResponse, ProfileParams>({
      queryFn: async ({ period, affAbo }, api, _extra, baseQuery) => {
        const query = `{
          aboDetail(period:"${period}",affAbo:"${affAbo}"){
            aboInfo {
                privacyFlag
                sponsor {
                  affNo
                  aboNo
                  name
                }
            }
            partyInfo {
              phoneNumbers {
                displayNumber
              }
              emailAddresses {
                email
              }
            }
          }
        }`;
        const queryParams = new URLSearchParams({ query: toSingleLine(query) });
        const result = (await baseQuery(getGraphqlPath(api, `/los/graphql?${queryParams}`))) as QueryReturnValue<
          ProfileResponse,
          FetchBaseQueryError,
          undefined
        >;
        if (result.error) return { error: result.error };
        const response = result.data;
        if (response) {
          const {
            data: {
              aboDetail: { aboInfo, partyInfo },
            },
          } = response;
          return { data: { aboInfo, partyInfo } };
        }
        return { data: {} };
      },
    }),
    getSponsorInfo: build.query<SponsorProfileTransformResponse, ProfileParams>({
      queryFn: async ({ period, affAbo }, api, _extra, baseQuery) => {
        const query = `{
          aboDetail(period:"${period}",affAbo:"${affAbo}"){
            aboInfo {
              affNo
              aboNo
              name
              localName
              privacyFlag
              isoLanguageCode
              phoneNumbers {
                  numbers {
                      displayNumber
                  }
              }
              emailAddresses {
                  addresses {
                      address
                  }
              }
              addresses {
                  addressLines
                  municipality
                  region
                  postalCode
                  country
                  type
              }
            }
          }
        }`;
        const queryParams = new URLSearchParams({ query: toSingleLine(query) });
        const result = (await baseQuery(getGraphqlPath(api, `/los/graphql?${queryParams}`))) as QueryReturnValue<
          SponsorProfileResponse,
          FetchBaseQueryError,
          undefined
        >;
        if (result.error) return { error: result.error };
        const response = result.data;
        if (response) {
          const {
            data: {
              aboDetail: { aboInfo },
            },
          } = response;
          return { data: { aboInfo } };
        }
        return { data: {} };
      },
    }),
  }),
});

export const { useGetProfileQuery, useGetContactInfoQuery, useGetSponsorInfoQuery } = profileAPI;
